// Nav.tsx - Fixed version
import React, { useState, useEffect, useRef } from 'react'
import {
  NavBarContainer,
  NavLogo,
  NavItems,
  DropIcon,
  DropdownItem,
  NavLink,
  AvatarWrapper,
  Menu,
  NavItemsz,
  Dropdowns,
  BurgerMenu,
  DropdownContents,
  MobileNav,
  MobileDrawer,
  MobileLink,
  CompanySelector,
  CategoryDivider,
  CompanyItem,
} from './navElements'
import useOwnUser from '../../../../hooks/useOwnUser'
import * as Response from '../../../../pkg/responses'
import user from '../../../../static/images/user.png'
import RequireRole from '../../../../features/auth/RequireRole'
import { useDispatch } from 'react-redux'
import { signOut } from '../../../../features/auth/authSlice'
import { apiSlice } from '../../../../api/index'
import { useTranslation } from 'react-i18next'
import {
  FaHome,
  FaUsers,
  FaCalendarAlt,
  FaCalendarCheck,
  FaBriefcase,
  FaUser,
  FaSignOutAlt,
  FaKey,
  FaBuilding,
} from 'react-icons/fa'

interface AvatarCircleProps {
  imageUrl: string
}

const AvatarCircle: React.FC<AvatarCircleProps> = ({ imageUrl }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleToggleMenu = () => setIsUserMenuOpen(!isUserMenuOpen)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuRef])

  return (
    <AvatarWrapper
      ref={menuRef}
      onClick={handleToggleMenu}
    >
      <img
        src={imageUrl}
        alt='Avatar'
      />
      <Menu isOpen={isUserMenuOpen}>
        <NavLink to='/settings'>
          <p>
            <FaKey size={16} /> {t('Root:Nav.ChangePassword')}
          </p>
        </NavLink>
        <p onClick={() => dispatch(signOut())}>
          <FaSignOutAlt size={16} /> {t('Root:Nav.Logout')}
        </p>
      </Menu>
    </AvatarWrapper>
  )
}

interface DropdownProps {
  children: React.ReactNode
  selectedNode: Response.CompanyProfile
}

export const Dropdown: React.FC<DropdownProps> = ({ children, selectedNode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  return (
    <CompanySelector
      ref={dropdownRef}
      onClick={handleToggle}
    >
      <FaBuilding size={16} />
      <h3>{selectedNode.name}</h3>
      <DropIcon style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
      {isOpen && <div className='company-dropdown'>{children}</div>}
    </CompanySelector>
  )
}

const VacationsDropdown: React.FC = () => {
  const { t } = useTranslation()
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const vacDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (vacDropdownRef.current && !vacDropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [vacDropdownRef])

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDropdownOpen(!isDropdownOpen)
  }

  return (
    <Dropdowns ref={vacDropdownRef}>
      <RequireRole allowedRoles={['owner', 'manager', 'employee']}>
        <NavItemsz onClick={toggleDropdown}>
          <FaCalendarAlt size={16} />
          {t('Root:Nav.Vacations')}
          <DropIcon style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
        </NavItemsz>
      </RequireRole>
      <DropdownContents isOpen={isDropdownOpen}>
        <RequireRole allowedRoles={['employee', 'manager', 'owner']}>
          <a href='/vacation'>
            <FaCalendarCheck size={16} />
            {t('Root:Nav.MyVacations')}
          </a>
        </RequireRole>
        <RequireRole allowedRoles={['owner', 'manager']}>
          <a href='/vacation/company-requests'>
            <FaCalendarAlt size={16} />
            {t('Root:Nav.Vacations')}
          </a>
        </RequireRole>
      </DropdownContents>
    </Dropdowns>
  )
}

const Nav = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { companyProfiles, selectedCompanyID, setSelectedCompanyID, setGroups } = useOwnUser()
  const [selected, setSelected] = useState<Response.CompanyProfile | undefined>(
    selectedCompanyID && companyProfiles ?
      companyProfiles.find((profile) => profile.id === selectedCompanyID)
    : undefined,
  )

  const handleProfileChange = (profile: Response.CompanyProfile): void => {
    setSelected(profile)
    setSelectedCompanyID(profile.id)
    setGroups(profile.groups)
    dispatch(apiSlice.util.resetApiState())
  }

  useEffect(() => {
    if (companyProfiles && !selected) {
      const defaultProfile =
        companyProfiles.find((profile) => profile.id === selectedCompanyID) || companyProfiles[0]
      if (defaultProfile) {
        handleProfileChange(defaultProfile)
      }
    }
  }, [companyProfiles, selectedCompanyID])

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false)
  const mobileDrawerRef = useRef<HTMLDivElement>(null)

  // Close mobile drawer when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isMobileDrawerOpen &&
        mobileDrawerRef.current &&
        !mobileDrawerRef.current.contains(event.target as Node)
      ) {
        const burgerButton = document.querySelector('[data-burger-button]')
        if (!burgerButton?.contains(event.target as Node)) {
          setIsMobileDrawerOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mobileDrawerRef, isMobileDrawerOpen])

  if (!selected) return <></>

  return (
    <>
      <NavBarContainer>
        <div className='desktop-nav'>
          <a href='/market'>
            <NavLogo>WHARF</NavLogo>
          </a>
        </div>

        <NavItems className='desktop-menu'>
          <VacationsDropdown />

          <RequireRole allowedRoles={['owner', 'manager']}>
            <NavLink to='/vacation/employees'>
              <FaUsers size={16} />
              {t('Root:Nav.Employees')}
            </NavLink>
          </RequireRole>

          <Dropdown selectedNode={selected}>
            {companyProfiles &&
              companyProfiles.map((profile: Response.CompanyProfile) => (
                <DropdownItem
                  key={profile.id}
                  onClick={() => handleProfileChange(profile)}
                >
                  <FaBuilding size={16} />
                  {profile.name}
                </DropdownItem>
              ))}
          </Dropdown>

          <AvatarCircle imageUrl={user} />
        </NavItems>

        <MobileNav>
          <a href='/market'>
            <NavLogo>WHARF</NavLogo>
          </a>

          <div className='mobile-controls'>
            <BurgerMenu
              data-burger-button
              isOpen={isMobileDrawerOpen}
              onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
            >
              <span />
              <span />
              <span />
            </BurgerMenu>
          </div>
        </MobileNav>
      </NavBarContainer>

      <MobileDrawer
        ref={mobileDrawerRef}
        isOpen={isMobileDrawerOpen}
      >
        <MobileLink
          isOpen={isMobileDrawerOpen}
          to='/market'
          onClick={() => setIsMobileDrawerOpen(false)}
        >
          <FaHome size={16} />
          {t('Root:Nav.Home')}
        </MobileLink>

        <RequireRole allowedRoles={['employee', 'manager', 'owner']}>
          <MobileLink
            isOpen={isMobileDrawerOpen}
            onClick={() => setIsMobileDrawerOpen(false)}
            to='/vacation'
          >
            <FaCalendarCheck size={16} />
            {t('Root:Nav.MyVacations')}
          </MobileLink>
        </RequireRole>

        <RequireRole allowedRoles={['owner', 'manager']}>
          <MobileLink
            isOpen={isMobileDrawerOpen}
            onClick={() => setIsMobileDrawerOpen(false)}
            to='/vacation/company-requests'
          >
            <FaCalendarAlt size={16} />
            {t('Root:Nav.Vacations')}
          </MobileLink>
        </RequireRole>

        <RequireRole allowedRoles={['owner', 'manager']}>
          <MobileLink
            isOpen={isMobileDrawerOpen}
            to='/vacation/employees'
            onClick={() => setIsMobileDrawerOpen(false)}
          >
            <FaUsers size={16} />
            {t('Root:Nav.Employees')}
          </MobileLink>
        </RequireRole>

        {companyProfiles && companyProfiles.length > 0 && (
          <>
            <CategoryDivider>{t('Root:Nav.Companies')}</CategoryDivider>
            {companyProfiles.map((profile: Response.CompanyProfile) => (
              <CompanyItem
                key={profile.id}
                onClick={() => {
                  handleProfileChange(profile)
                  setIsMobileDrawerOpen(false)
                }}
                style={{
                  background: selected.id === profile.id ? '#e0e6ed' : '#f5f7fa',
                  fontWeight: selected.id === profile.id ? '600' : '500',
                }}
              >
                <FaBuilding size={16} />
                {profile.name}
              </CompanyItem>
            ))}
          </>
        )}

        <CategoryDivider>{t('Root:Nav.Account')}</CategoryDivider>

        <MobileLink
          isOpen={isMobileDrawerOpen}
          to='/settings'
          onClick={() => setIsMobileDrawerOpen(false)}
        >
          <FaKey size={16} />
          {t('Root:Nav.ChangePassword')}
        </MobileLink>

        <CompanyItem onClick={() => dispatch(signOut())}>
          <FaSignOutAlt size={16} />
          {t('Root:Nav.Logout')}
        </CompanyItem>
      </MobileDrawer>
    </>
  )
}

export default Nav
